import { getConsent } from '@/lib/consent';
import { clearBrowserCookies, clearWebStorage } from '@/lib/storage/browserStorage';
import { NEXT_PUBLIC_ADALYSER_DISABLED } from '@/lib/env';

const UK_SITE_ID = '8';
const ADALYSER_CLIENT_ID = 'firsttable';
const ADALYSER_SESSION_NAME = 'lce1';
export const ADALYSER_SCRIPT_URL = `https://c0.adalyser.com/adalyser.js?cid=${ADALYSER_CLIENT_ID}`;
const ADALYSER_DISABLE_FLAG = `__adal_disable_${ADALYSER_CLIENT_ID}`;
const ADALYSER_TRACKER_NAME = 'adalyserTracker';

// Live cookies are `__adal_*`; pending hits are `adal_out_queue_*`. `_adal_` covers leftover one-underscore cookie forms. Prefix-matched so withdraw teardown clears the full set.
const ADALYSER_KEY_PREFIXES = ['_adal_', '__adal_', 'adal_out_queue'];

const MAX_PENDING_EVENTS = 20;

type AdalyserTracker = {
  (...args: unknown[]): void;
  q?: unknown[][];
};

type PendingEvent = [eventName: string, data: Record<string, unknown>];

declare global {
  interface Window {
    adalyserTracker?: AdalyserTracker;
    GlobalAdalyserNamespace?: string[];
    [disableFlag: `__adal_disable_${string}`]: boolean | undefined;
  }
}

let enabled = false;
let initialised = false;
let pendingEvents: PendingEvent[] = [];

export const isAdalyserConfigured = (): boolean =>
  !NEXT_PUBLIC_ADALYSER_DISABLED && process.env.NEXT_PUBLIC_SITE_ID === UK_SITE_ID;

export const isAdalyserEnabled = (): boolean => enabled && isAdalyserConfigured();

const isAdalyserKey = (key: string): boolean =>
  ADALYSER_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));

const getDisableFlag = (): boolean => Boolean(window[ADALYSER_DISABLE_FLAG]);

const setDisableFlag = (disabled: boolean): void => {
  if (disabled) {
    window[ADALYSER_DISABLE_FLAG] = true;
    return;
  }

  delete window[ADALYSER_DISABLE_FLAG];
};

const clearAdalyserStorage = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  clearBrowserCookies(isAdalyserKey);
  clearWebStorage(() => window.localStorage, isAdalyserKey);
  clearWebStorage(() => window.sessionStorage, isAdalyserKey);
};

export const ensureAdalyserQueue = (): void => {
  if (typeof window === 'undefined' || getDisableFlag() || window.adalyserTracker) {
    return;
  }

  window.GlobalAdalyserNamespace = window.GlobalAdalyserNamespace || [];
  window.GlobalAdalyserNamespace.push(ADALYSER_TRACKER_NAME);
  window.adalyserTracker = function adalyserTracker(...args: unknown[]) {
    const tracker = window.adalyserTracker as AdalyserTracker;
    tracker.q = tracker.q || [];
    tracker.q.push(args);
  };
  window.adalyserTracker.q = window.adalyserTracker.q || [];
};

const flushAdalyserEvents = (): void => {
  const events = pendingEvents;
  pendingEvents = [];
  events.forEach(([eventName, data]) =>
    window.adalyserTracker?.('trackEvent', eventName, data, true)
  );
};

export const initialiseAdalyser = (): void => {
  if (!isAdalyserEnabled() || typeof window.adalyserTracker !== 'function') {
    return;
  }

  window.adalyserTracker('create', {
    campaignCookieTimeout: 15552000,
    conversionCookieTimeout: 604800,
    clientId: ADALYSER_CLIENT_ID,
    trafficSourceInternalReferrers: ['^(.*\\.)?firsttable.*$'],
  });
  window.adalyserTracker('trackSession', ADALYSER_SESSION_NAME);
  initialised = true;
  flushAdalyserEvents();
};

// adalyser.js replays its own command queue as soon as it loads, so a conversion pushed onto the tracker before `create` runs would be processed ahead of it. Buffer locally until init instead.
export const sendAdalyserEvent = (eventName: string, data: Record<string, unknown> = {}): void => {
  if (!isAdalyserEnabled()) {
    return;
  }

  if (initialised && typeof window.adalyserTracker === 'function') {
    window.adalyserTracker('trackEvent', eventName, data, true);
    return;
  }

  pendingEvents = [...pendingEvents.slice(-(MAX_PENDING_EVENTS - 1)), [eventName, data]];
};

export const enableAdalyser = (): boolean => {
  if (typeof window === 'undefined' || !isAdalyserConfigured() || !getConsent().allowed) {
    enabled = false;
    return false;
  }

  setDisableFlag(false);
  ensureAdalyserQueue();
  enabled = true;
  return true;
};

export const disableAdalyser = (): void => {
  enabled = false;
  initialised = false;
  pendingEvents = [];
  if (typeof window === 'undefined') {
    return;
  }

  setDisableFlag(true);
  clearAdalyserStorage();
};
